import React, { useState } from "react";
import {
  Grid,
  Button,
  Snackbar,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import { CheckCircle, Error as ErrorIcon } from "@material-ui/icons";
import RetentionMetricsCard from "./RetentionMetricsCard";
import CohortHeatmap from "./CohortHeatmap";
import EngagementFunnel from "./EngagementFunnel";
import AtRiskUsersList from "./AtRiskUsersList";
import RetentionTrends from "./RetentionTrends";
import LiveMetricsCard from "./LiveMetricsCard";
import InfoTooltip from "./InfoTooltip";
import BuildHistoricalDataModal from "./BuildHistoricalDataModal";
import BuildProgressButton from "./BuildProgressButton";
import { PrimaryText } from "components/Common/PrimaryText";
import { View } from "react-native-web";
import { useRetentionData } from "hooks/useRetentionData";
import { useDisplay } from "context/DisplayContext";

const RetentionDashboard = () => {
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [filter, setFilter] = useState("6months"); // Updated filter values

  const { isMobile } = useDisplay();

  // Fetch all data once based on filter
  const { data, loadingStates, loading, error } = useRetentionData(filter);

  const { metrics, dailySnapshots, cohortData, engagementMetrics } = data || {};

  // Debug logging
  console.log("RetentionDashboard - data:", data);
  console.log("RetentionDashboard - engagementMetrics:", engagementMetrics);

  const handleBuildSuccess = (message) => {
    setSnackbar({
      open: true,
      message: message,
      severity: "success",
    });
  };

  return (
    <View
      style={{
        flex: 1,
        width: "100%",
        padding: isMobile ? 12 : 20,
      }}
    >
      {/* Dashboard Header */}
      <View style={{ marginBottom: isMobile ? 16 : 30 }}>
        <View
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 8 : 0,
            marginBottom: 8,
          }}
        >
          <PrimaryText fontSize={isMobile ? 22 : 28} fontWeight={700}>
            User Retention Dashboard
            <InfoTooltip
              title="What is this dashboard?"
              content={
                <div style={{ maxWidth: 400 }}>
                  <h4 style={{ margin: "0 0 8px 0", fontSize: 16 }}>
                    Understanding Your Retention Dashboard
                  </h4>
                  <p style={{ margin: "0 0 12px 0", fontSize: 14 }}>
                    This dashboard provides comprehensive insights into user
                    behavior and retention patterns to help you make data-driven
                    decisions.
                  </p>

                  <h5
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    Key Metrics Explained:
                  </h5>
                  <ul
                    style={{
                      margin: "0 0 12px 0",
                      paddingLeft: 20,
                      fontSize: 13,
                    }}
                  >
                    <li>
                      <strong>Live Activity:</strong> Real-time user signups and
                      activity
                    </li>
                    <li>
                      <strong>Engagement Funnel:</strong> How users interact
                      with your app (messages, swipes, matches)
                    </li>
                    <li>
                      <strong>Retention Trends:</strong> Daily/weekly patterns
                      of user return rates
                    </li>
                    <li>
                      <strong>Cohort Analysis:</strong> Retention rates for user
                      groups over time
                    </li>
                    <li>
                      <strong>At-Risk Users:</strong> Users who may need
                      re-engagement
                    </li>
                  </ul>

                  <h5
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    Time Filters:
                  </h5>
                  <p style={{ margin: "0 0 12px 0", fontSize: 13 }}>
                    Use the dropdown to analyze different periods. Current week
                    data updates in real-time!
                  </p>

                  <p style={{ margin: "0", fontSize: 13, fontStyle: "italic" }}>
                    💡 Tip: Click on user avatars to see detailed information.
                    Hover over chart dots for exact values.
                  </p>
                </div>
              }
            />
          </PrimaryText>
          <View
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 8 : 12,
              flexDirection: "row",
              flexWrap: "wrap",
              width: isMobile ? "100%" : undefined,
            }}
          >
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setShowBuildModal(true)}
              style={{
                textTransform: "none",
                height: 32,
                fontSize: 14,
                fontWeight: 500,
                width: isMobile ? "100%" : undefined,
              }}
            >
              Build Historical Data
            </Button>
            <FormControl
              variant="outlined"
              size="small"
              style={{ width: isMobile ? "100%" : undefined }}
            >
              <InputLabel>Time Range</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label="Time Range"
              >
                <MenuItem value="1year">Last Year</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="3months">Last 3 Months</MenuItem>
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="2weeks">Last 2 Weeks</MenuItem>
                <MenuItem value="1week">Last Week</MenuItem>
              </Select>
            </FormControl>
          </View>
        </View>
        <PrimaryText fontSize={14} style={{ marginTop: 8, color: "#666" }}>
          Monitor user engagement, track retention patterns, and identify
          opportunities to reduce churn.
        </PrimaryText>
      </View>

      {/* First Row - Key Metrics */}
      <Grid container spacing={isMobile ? 2 : 3} style={{ marginBottom: isMobile ? 16 : 30 }}>
        <Grid item xs={12}>
          <RetentionMetricsCard
            metrics={metrics}
            loading={loading}
            error={error}
            filter={filter}
          />
        </Grid>
      </Grid>

      {/* Second Row - Cohort and Live Metrics */}
      <Grid container spacing={isMobile ? 2 : 3} style={{ marginBottom: isMobile ? 16 : 30 }}>
        <Grid item xs={12} md={8}>
          <CohortHeatmap
            cohortData={cohortData}
            loading={loadingStates?.cohorts || loading}
            error={error}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <LiveMetricsCard />
        </Grid>
      </Grid>

      {/* Third Row - Retention Trends */}
      <Grid container spacing={isMobile ? 2 : 3} style={{ marginBottom: isMobile ? 16 : 30 }}>
        <Grid item xs={12}>
          <RetentionTrends
            filter={filter}
            dailySnapshots={dailySnapshots}
            loading={loadingStates?.snapshots || loading}
            error={error}
          />
        </Grid>
      </Grid>

      {/* Fourth Row - Engagement Funnel and At-Risk Users */}
      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12} md={6}>
          <EngagementFunnel
            metrics={{ engagementMetrics: engagementMetrics }}
            loading={loading}
            error={error}
            filter={filter}
            engagementLoading={loadingStates?.engagement}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AtRiskUsersList filter={filter} />
        </Grid>
      </Grid>

      {/* Build Historical Data Modal */}
      <BuildHistoricalDataModal
        open={showBuildModal}
        onClose={() => setShowBuildModal(false)}
        onSuccess={handleBuildSuccess}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Paper
          style={{
            padding: "12px 24px",
            backgroundColor:
              snackbar.severity === "success" ? "#43a047" : "#e53935",
            color: "white",
            display: "flex",
            alignItems: "center",
            minWidth: isMobile ? "auto" : 300,
          }}
        >
          {snackbar.severity === "success" ? (
            <CheckCircle style={{ marginRight: 12 }} />
          ) : (
            <ErrorIcon style={{ marginRight: 12 }} />
          )}
          <PrimaryText fontSize={14} style={{ color: "white" }}>
            {snackbar.message}
          </PrimaryText>
        </Paper>
      </Snackbar>

      {/* Build Progress Button */}
      <BuildProgressButton />
    </View>
  );
};

export default RetentionDashboard;
